import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TEKSModal from "../components/TEKSModal";
import ScopeSequenceViewer from "../components/ScopeSequenceViewer";
import { calendarAPI, kioskAPI, teacherAPI, teksAPI, walkthroughAPI } from "../services/api";
import {
  parseObjectiveText,
  parseObjectiveTextToStructured,
  publishKioskPayload,
  serializeStructuredObjectives,
  validateStructuredObjectives,
} from "../utils/kiosk";
import "./WeeklyPlannerPage.css";

const toDateKey = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) return raw.split("T")[0];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split("T")[0];
};

const fromDateKey = (dateKey) => {
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatDate = (dateKey) => {
  const parsed = fromDateKey(dateKey);
  if (!parsed) return "TBD";
  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const addDays = (dateKey, days) => {
  const parsed = fromDateKey(dateKey);
  if (!parsed) return null;
  parsed.setDate(parsed.getDate() + days);
  return parsed.toISOString().split("T")[0];
};

const mondayOfWeek = (date = new Date()) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  const day = copy.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + offset);
  return copy.toISOString().split("T")[0];
};

const plannerStorageKey = (courseId, weekStart) =>
  `weekly-planner:${courseId || "none"}:${weekStart || "none"}`;
const STRATEGY_INSERT_DRAFT_KEY = "planning_hub_strategy_insert_draft_v1";

const toResourceLinks = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({
      title: String(item?.title || "").trim(),
      url: String(item?.url || "").trim(),
      notes: String(item?.notes || "").trim(),
    }))
    .filter((item) => item.title || item.url || item.notes);
};

const toTextArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || "").trim()).filter(Boolean);
};

const toStrategyCards = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const title = String(item?.title || "").trim();
      if (!title) return null;
      return {
        strategy_id: String(item?.strategy_id || item?.strategyId || "").trim() || null,
        strategy_seed_key:
          String(item?.strategy_seed_key || item?.strategySeedKey || "").trim() || null,
        title,
        category: String(item?.category || "").trim() || null,
        visibility_scope:
          String(item?.visibility_scope || item?.visibilityScope || "").trim() || null,
        summary: String(item?.summary || "").trim() || null,
        sample_usage: String(item?.sample_usage || item?.sampleUsage || "").trim() || null,
        how_to_use_steps: toTextArray(item?.how_to_use_steps || item?.howToUseSteps),
        look_fors: toTextArray(item?.look_fors || item?.lookFors),
        source_title: String(item?.source_title || item?.sourceTitle || "").trim() || null,
        source_url: String(item?.source_url || item?.sourceUrl || "").trim() || null,
        source_pages: String(item?.source_pages || item?.sourcePages || "").trim() || null,
        reference_url:
          String(item?.reference_url || item?.referenceUrl || "").trim() || null,
        added_at: String(item?.added_at || item?.addedAt || "").trim() || null,
      };
    })
    .filter(Boolean);
};

const formatKioskEntriesForPlanner = (entries = []) =>
  (entries || [])
    .map((entry) => {
      const lines = [];
      if (entry.teksCode) lines.push(entry.teksCode);
      if (entry.statement) lines.push(entry.statement);
      (entry.iCanStatements || [])
        .map((line) => String(line || "").trim())
        .filter(Boolean)
        .forEach((line) => {
          const normalized = line.replace(/^[\u2022*\-\s]+/, "");
          lines.push(`• ${normalized}`);
        });
      return lines.join("\n").trim();
    })
    .filter(Boolean)
    .join("\n\n")
    .trim();

const toPlannerPublishState = ({ publishedAt, updatedAt }) => {
  if (!publishedAt) return "draft";
  if (!updatedAt) return "published";
  const publishedMs = new Date(publishedAt).getTime();
  const updatedMs = new Date(updatedAt).getTime();
  if (Number.isNaN(publishedMs) || Number.isNaN(updatedMs)) return "published";
  return updatedMs > publishedMs ? "needs_republish" : "published";
};

const normalizeTeksToken = (value) => String(value || "").trim().toLowerCase();

const matchesAllowedTeksToken = (rawToken, allowedTokens) => {
  const normalizedToken = normalizeTeksToken(rawToken);
  if (!normalizedToken) return false;
  if (allowedTokens.has(normalizedToken)) return true;
  if (!normalizedToken.includes("/")) return false;

  const slashParts = normalizedToken
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (slashParts.length < 2) return false;
  return slashParts.every((part) => allowedTokens.has(part));
};

const plannerPublishLabel = (publishState) => {
  if (publishState === "published") return "Published";
  if (publishState === "needs_republish") return "Edited after publish";
  return "Draft";
};

const plannerPublishClass = (publishState) => {
  if (publishState === "published") return "is-published";
  if (publishState === "needs_republish") return "is-needs-republish";
  return "is-draft";
};

const performanceTaskPathByAsset = (asset = {}) => {
  const title = String(asset.title || "").toLowerCase();
  const type = String(asset.type || "").toLowerCase();
  const isPerformanceTask = type === "performance task" || title.includes("performance task");
  if (!isPerformanceTask) return null;
  if (title.includes("19th amendment")) return "/performance-tasks/unit1-19th-amendment";
  if (title.includes("civil rights")) return "/performance-tasks/unit5-civil-rights";
  if (title.includes("2001 as a turning point") || title.includes("modern era")) {
    return "/performance-tasks/unit6-modern-era";
  }
  if (
    title.includes("american government in action") ||
    title.includes("branches hearing simulation")
  ) {
    return "/performance-tasks/us-government-unit3-branches";
  }
  return "/performance-tasks";
};

const WeeklyPlannerPage = ({ courseId }) => {
  const [weekStart, setWeekStart] = useState(mondayOfWeek());
  const [loading, setLoading] = useState(false);
  const [calendarRows, setCalendarRows] = useState([]);
  const [lessonDetails, setLessonDetails] = useState({});
  const [selectedTeksCode, setSelectedTeksCode] = useState(null);
  const [plan, setPlan] = useState({
    objectivesByDate: {},
    notesByDate: {},
    resourcesByDate: {},
    strategyCardsByDate: {},
  });
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [objectiveImagesByDate, setObjectiveImagesByDate] = useState({});
  const [plannerPublishMetaByDate, setPlannerPublishMetaByDate] = useState({});
  const [publishingPlannerDateKey, setPublishingPlannerDateKey] = useState(null);
  const [publishingPlannerWeek, setPublishingPlannerWeek] = useState(false);
  const [plannerPublishError, setPlannerPublishError] = useState("");
  const [plannerPublishSuccess, setPlannerPublishSuccess] = useState("");
  const [courseTeksOptions, setCourseTeksOptions] = useState([]);
  const [courseTeksLoading, setCourseTeksLoading] = useState(false);
  const [courseTeksError, setCourseTeksError] = useState("");
  const [activeObjectiveTeksKey, setActiveObjectiveTeksKey] = useState("");
  const [objectiveTeksSearchInput, setObjectiveTeksSearchInput] = useState("");
  const [debouncedObjectiveTeksSearch, setDebouncedObjectiveTeksSearch] = useState("");
  const [publishingDateKey, setPublishingDateKey] = useState(null);
  const [publishedDateKey, setPublishedDateKey] = useState(null);
  const [publishErrorByDate, setPublishErrorByDate] = useState({});
  const [insertNotice, setInsertNotice] = useState("");
  const [restoringDefaultDateKey, setRestoringDefaultDateKey] = useState(null);
  const [strategyPickerOpen, setStrategyPickerOpen] = useState(false);
  const [strategyPickerDateKey, setStrategyPickerDateKey] = useState(null);
  const [strategyPickerQuery, setStrategyPickerQuery] = useState("");
  const [strategyPickerLoading, setStrategyPickerLoading] = useState(false);
  const [strategyPickerError, setStrategyPickerError] = useState("");
  const [strategyPickerRows, setStrategyPickerRows] = useState([]);
  const [addingStrategyId, setAddingStrategyId] = useState(null);
  const [expandedStrategyCardKeys, setExpandedStrategyCardKeys] = useState({});
  const [isDayListSticky, setIsDayListSticky] = useState(false);
  const [reviewedDates, setReviewedDates] = useState(new Set());
  const persistTimeoutsRef = useRef({});
  const stickyTriggerRef = useRef(null);
  const activeObjectiveTeksKeyRef = useRef("");
  const objectiveTextareaRef = useRef(null);
  const notesTextareaRef = useRef(null);

  const weekEnd = useMemo(() => addDays(weekStart, 4), [weekStart]);

  useEffect(() => {
    if (!courseId) {
      setCourseTeksOptions([]);
      setCourseTeksError("");
      setCourseTeksLoading(false);
      return;
    }

    let cancelled = false;
    const loadCourseTeks = async () => {
      setCourseTeksLoading(true);
      setCourseTeksError("");
      try {
        const response = await teksAPI.getAll(courseId);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        const options = rows
          .map((row) => ({
            code: String(row?.teks_code || "").trim(),
            description: String(row?.description || row?.teks_description || "").trim(),
          }))
          .filter((row) => row.code)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
        setCourseTeksOptions(options);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading course TEKS for planner:", error);
        setCourseTeksOptions([]);
        setCourseTeksError("Could not load TEKS for this course.");
      } finally {
        if (!cancelled) setCourseTeksLoading(false);
      }
    };

    loadCourseTeks();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedObjectiveTeksSearch(objectiveTeksSearchInput);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [objectiveTeksSearchInput]);

  useEffect(() => {
    activeObjectiveTeksKeyRef.current = activeObjectiveTeksKey;
  }, [activeObjectiveTeksKey]);

  useEffect(() => {
    setActiveObjectiveTeksKey("");
    setObjectiveTeksSearchInput("");
    setDebouncedObjectiveTeksSearch("");
  }, [courseId, selectedDateKey]);

  useEffect(() => {
    const persisted = localStorage.getItem(plannerStorageKey(courseId, weekStart));
    if (!persisted) {
      setPlan({
        objectivesByDate: {},
        notesByDate: {},
        resourcesByDate: {},
        strategyCardsByDate: {},
      });
      return;
    }
    try {
      const parsed = JSON.parse(persisted);
      setPlan({
        objectivesByDate: parsed.objectivesByDate || {},
        notesByDate: parsed.notesByDate || {},
        resourcesByDate: parsed.resourcesByDate || {},
        strategyCardsByDate: parsed.strategyCardsByDate || {},
      });
    } catch (error) {
      console.error("Failed to parse planner state:", error);
      setPlan({
        objectivesByDate: {},
        notesByDate: {},
        resourcesByDate: {},
        strategyCardsByDate: {},
      });
    }
  }, [courseId, weekStart]);

  useEffect(() => {
    if (!courseId || !weekStart || !weekEnd) {
      setCalendarRows([]);
      setLessonDetails({});
      return;
    }

    const loadWeek = async () => {
      setLoading(true);
      try {
        const calendarResponse = await calendarAPI.getRange(courseId, weekStart, weekEnd, {
          includeAssets: true,
        });
        const rows = (calendarResponse.data || []).map((row) => ({
          ...row,
          _dateKey: toDateKey(row.date),
        }));

        setCalendarRows(rows);

        const detailMap = {};
        rows.forEach((row) => {
          if (!row.lesson_id || detailMap[row.lesson_id]) return;
          detailMap[row.lesson_id] = {
            id: row.lesson_id,
            lesson_title: row.lesson_title || "",
            chapter_ref: row.chapter_ref || "",
            unit_number: row.unit_number || null,
            assets: Array.isArray(row.lesson_assets) ? row.lesson_assets : [],
          };
        });
        setLessonDetails(detailMap);
      } catch (error) {
        console.error("Error loading weekly planner:", error);
        setCalendarRows([]);
        setLessonDetails({});
      } finally {
        setLoading(false);
      }
    };

    loadWeek();
  }, [courseId, weekEnd, weekStart]);

  useEffect(() => {
    if (!courseId || !weekStart || !weekEnd) return;

    let cancelled = false;
    const loadSeededObjectives = async () => {
      try {
        const response = await kioskAPI.getObjectivesRange(courseId, weekStart, weekEnd);
        if (cancelled) return;

        const rows = Array.isArray(response.data) ? response.data : [];
        if (!rows.length) return;

        const grouped = rows.reduce((acc, row) => {
          const dateKey = toDateKey(row.calendar_date);
          if (!dateKey) return acc;
          if (!acc[dateKey]) acc[dateKey] = [];
          acc[dateKey].push({
            order: row.objective_order || 1,
            teksCode: row.teks_code || "",
            statement: row.objective_statement || "",
            imageUrl: row.objective_image_url || "",
            iCanStatements: Array.isArray(row.i_can_statements) ? row.i_can_statements : [],
          });
          return acc;
        }, {});

        const imageMap = {};
        Object.entries(grouped).forEach(([dateKey, entries]) => {
          const sorted = entries.sort((a, b) => a.order - b.order);
          imageMap[dateKey] = sorted
            .map((entry) => String(entry.imageUrl || "").trim())
            .filter(Boolean);
        });
        setObjectiveImagesByDate(imageMap);

        setPlan((prev) => {
          const nextObjectivesByDate = { ...prev.objectivesByDate };
          Object.entries(grouped).forEach(([dateKey, entries]) => {
            const current = String(nextObjectivesByDate[dateKey] || "").trim();
            if (current) return;
            const sortedEntries = entries.sort((a, b) => a.order - b.order);
            nextObjectivesByDate[dateKey] = formatKioskEntriesForPlanner(sortedEntries);
          });

          const next = { ...prev, objectivesByDate: nextObjectivesByDate };
          localStorage.setItem(plannerStorageKey(courseId, weekStart), JSON.stringify(next));
          return next;
        });
      } catch (error) {
        console.error("Error loading seeded kiosk objectives into planner:", error);
      }
    };

    loadSeededObjectives();
    return () => {
      cancelled = true;
    };
  }, [courseId, weekEnd, weekStart]);

  const workWeekDates = useMemo(() => {
    return Array.from({ length: 5 }, (_, idx) => addDays(weekStart, idx));
  }, [weekStart]);

  useEffect(() => {
    setPlannerPublishMetaByDate({});
    setPlannerPublishError("");
    setPlannerPublishSuccess("");
  }, [courseId, weekStart]);

  const rowsByDate = useMemo(() => {
    const map = {};
    calendarRows.forEach((row) => {
      if (row._dateKey) map[row._dateKey] = row;
    });
    return map;
  }, [calendarRows]);

  const weekRows = useMemo(
    () =>
      workWeekDates.map((dateKey) => ({
        dateKey,
        row: rowsByDate[dateKey] || null,
      })),
    [rowsByDate, workWeekDates],
  );

  useEffect(() => {
    if (!weekRows.length) {
      setSelectedDateKey(null);
      return;
    }

    const isCurrentSelectionInWeek = weekRows.some(
      (item) => item.dateKey === selectedDateKey,
    );
    if (!isCurrentSelectionInWeek) {
      setSelectedDateKey(weekRows[0].dateKey);
    }
  }, [selectedDateKey, weekRows]);

  const summary = useMemo(() => {
    const rows = weekRows.map((item) => item.row).filter(Boolean);
    const plannedRows = rows.filter((row) => row.lesson_id);
    const teks = [...new Set(plannedRows.map((row) => row.teks_code).filter(Boolean))].sort();
    const totalResources = plannedRows.reduce((acc, row) => {
      const detail = lessonDetails[row.lesson_id];
      return acc + (detail?.assets?.length || 0);
    }, 0);
    const milestoneCount = rows.filter((row) => row.milestone).length;
    return {
      plannedDays: plannedRows.length,
      teks,
      totalResources,
      milestoneCount,
    };
  }, [lessonDetails, weekRows]);

  const activeWeekUnitNumber = useMemo(() => {
    const firstLessonRow = weekRows.find((item) => item.row?.lesson_id);
    if (!firstLessonRow) return null;
    const detail = lessonDetails[firstLessonRow.row.lesson_id];
    return detail?.unit_number || null;
  }, [lessonDetails, weekRows]);

  const selectedDay = useMemo(() => {
    if (!weekRows.length) return null;
    return (
      weekRows.find((item) => item.dateKey === selectedDateKey) || weekRows[0]
    );
  }, [selectedDateKey, weekRows]);

  const selectedDayDateKey = selectedDay?.dateKey || null;
  const selectedDayRow = selectedDay?.row || null;
  const selectedDayLesson = useMemo(
    () =>
      selectedDayRow?.lesson_id
        ? lessonDetails[selectedDayRow.lesson_id]
        : null,
    [lessonDetails, selectedDayRow?.lesson_id],
  );
  const selectedDayAssets = selectedDayLesson?.assets || [];
  const selectedObjectiveValue =
    (selectedDayDateKey && plan.objectivesByDate[selectedDayDateKey]) || "";
  const selectedNotesValue =
    (selectedDayDateKey && plan.notesByDate[selectedDayDateKey]) || "";
  const selectedDayStrategyCards =
    (selectedDayDateKey && toStrategyCards(plan.strategyCardsByDate[selectedDayDateKey])) || [];
  const selectedDayPublishMeta =
    (selectedDayDateKey && plannerPublishMetaByDate[selectedDayDateKey]) || null;
  const selectedDayPublishState = toPlannerPublishState({
    publishedAt: selectedDayPublishMeta?.publishedAt || null,
    updatedAt: selectedDayPublishMeta?.updatedAt || null,
  });
  const selectedObjectiveRows = useMemo(
    () =>
      parseObjectiveTextToStructured(
        selectedObjectiveValue,
        selectedDayRow?.teks_code || "",
      ),
    [selectedDayRow?.teks_code, selectedObjectiveValue],
  );
  const filteredCourseTeksOptions = useMemo(() => {
    const query = String(debouncedObjectiveTeksSearch || "").trim().toLowerCase();
    const rows = courseTeksOptions.filter((option) => {
      if (!query) return true;
      return (
        String(option.code || "").toLowerCase().includes(query)
        || String(option.description || "").toLowerCase().includes(query)
      );
    });
    return rows.slice(0, 12);
  }, [courseTeksOptions, debouncedObjectiveTeksSearch]);
  const validateObjectivesForCourse = useCallback(
    (rows, fallbackTeksCode = "") => {
      const base = validateStructuredObjectives(rows);
      if (!courseTeksOptions.length) return base;

      const allowedCodes = new Set(
        courseTeksOptions
          .map((option) => normalizeTeksToken(option.code))
          .filter(Boolean),
      );
      const fallbackCode = normalizeTeksToken(fallbackTeksCode);
      if (fallbackCode) allowedCodes.add(fallbackCode);

      const courseErrors = [];
      (Array.isArray(rows) ? rows : []).forEach((objective, index) => {
        const teksCode = String(objective?.teksCode || "").trim();
        if (!teksCode) return;
        if (!matchesAllowedTeksToken(teksCode, allowedCodes)) {
          courseErrors.push(`Objective ${index + 1}: choose a TEKS from this course list.`);
        }
      });

      return {
        ...base,
        isValid: [...base.errors, ...courseErrors].length === 0,
        errors: [...base.errors, ...courseErrors],
      };
    },
    [courseTeksOptions],
  );
  const selectedObjectiveValidation = useMemo(
    () => validateObjectivesForCourse(selectedObjectiveRows, selectedDayRow?.teks_code || ""),
    [selectedDayRow?.teks_code, selectedObjectiveRows, validateObjectivesForCourse],
  );

  const autoResizeTextarea = useCallback((element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!courseId || !weekStart || !weekEnd) return;
    let cancelled = false;

    const loadTeacherPlannerEntries = async () => {
      try {
        const response = await teacherAPI.getPlannerEntries(courseId, weekStart, weekEnd);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        const nextPublishMetaByDate = {};

        rows.forEach((row) => {
          const dateKey = toDateKey(row.calendar_date);
          if (!dateKey) return;
          nextPublishMetaByDate[dateKey] = {
            publishedAt: row.published_at || null,
            updatedAt: row.updated_at || null,
            publishState: row.publish_state
              || toPlannerPublishState({
                publishedAt: row.published_at || null,
                updatedAt: row.updated_at || null,
              }),
          };
        });
        setPlannerPublishMetaByDate(nextPublishMetaByDate);

        if (rows.length === 0) return;

        setPlan((prev) => {
          const nextObjectives = { ...prev.objectivesByDate };
          const nextNotes = { ...prev.notesByDate };
          const nextResources = { ...prev.resourcesByDate };
          const nextStrategyCards = { ...prev.strategyCardsByDate };

          rows.forEach((row) => {
            const dateKey = toDateKey(row.calendar_date);
            if (!dateKey) return;

            if (typeof row.objective_override === "string") {
              nextObjectives[dateKey] = row.objective_override;
            }
            if (typeof row.notes === "string") {
              nextNotes[dateKey] = row.notes;
            }
            nextResources[dateKey] = toResourceLinks(row.resource_links);
            nextStrategyCards[dateKey] = toStrategyCards(row.strategy_cards);
          });

          const next = {
            ...prev,
            objectivesByDate: nextObjectives,
            notesByDate: nextNotes,
            resourcesByDate: nextResources,
            strategyCardsByDate: nextStrategyCards,
          };
          localStorage.setItem(plannerStorageKey(courseId, weekStart), JSON.stringify(next));
          return next;
        });
      } catch (error) {
        console.error("Error loading teacher planner entries:", error);
      }
    };

    loadTeacherPlannerEntries();
    return () => {
      cancelled = true;
    };
  }, [courseId, weekEnd, weekStart]);

  // Load principal-reviewed badges for this week (teacher role only)
  useEffect(() => {
    if (!weekStart || !weekEnd) return;
    let cancelled = false;

    const loadReviewed = async () => {
      try {
        const response = await walkthroughAPI.getMyReviewed({
          dateFrom: weekStart,
          dateTo: weekEnd,
        });
        if (cancelled) return;
        const obs = Array.isArray(response.data) ? response.data : [];
        const dates = new Set(
          obs
            .filter((o) => o.isVisibleToTeacher)
            .map((o) => {
              const raw = String(o.observationDate || "");
              return raw.includes("T") ? raw.split("T")[0] : raw.substring(0, 10);
            })
            .filter(Boolean),
        );
        setReviewedDates(dates);
      } catch (_error) {
        // Non-critical — silently ignore (e.g. 403 if user is admin, not teacher)
        if (!cancelled) setReviewedDates(new Set());
      }
    };

    loadReviewed();
    return () => {
      cancelled = true;
    };
  }, [weekEnd, weekStart]);

  const persistPlannerDay = useCallback(
    async (dateKey, nextPlan) => {
      if (!courseId || !dateKey) return;
      try {
        await teacherAPI.upsertPlannerEntry({
          courseId,
          date: dateKey,
          objectiveOverride: nextPlan.objectivesByDate[dateKey] || "",
          notes: nextPlan.notesByDate[dateKey] || "",
          resourceLinks: toResourceLinks(nextPlan.resourcesByDate[dateKey]),
          strategyCards: toStrategyCards(nextPlan.strategyCardsByDate[dateKey]),
        });
      } catch (error) {
        console.error("Error saving teacher planner entry:", error);
      }
    },
    [courseId],
  );

  const schedulePersistPlannerDay = useCallback(
    (dateKey, nextPlan) => {
      if (!dateKey) return;
      if (persistTimeoutsRef.current[dateKey]) {
        window.clearTimeout(persistTimeoutsRef.current[dateKey]);
      }
      persistTimeoutsRef.current[dateKey] = window.setTimeout(() => {
        persistPlannerDay(dateKey, nextPlan);
      }, 500);
    },
    [persistPlannerDay],
  );

  const markPlannerDayEdited = useCallback((dateKey) => {
    if (!dateKey) return;
    setPlannerPublishMetaByDate((prev) => {
      const current = prev[dateKey] || {};
      const publishedAt = current.publishedAt || null;
      return {
        ...prev,
        [dateKey]: {
          publishedAt,
          updatedAt: new Date().toISOString(),
          publishState: publishedAt ? "needs_republish" : "draft",
        },
      };
    });
  }, []);

  useEffect(
    () => () => {
      Object.values(persistTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      persistTimeoutsRef.current = {};
    },
    [],
  );

  useLayoutEffect(() => {
    const resize = () => {
      autoResizeTextarea(objectiveTextareaRef.current);
      autoResizeTextarea(notesTextareaRef.current);
    };

    resize();

    // Run a couple follow-up passes to catch async content/layout settling.
    const rafId = window.requestAnimationFrame(() => {
      resize();
      window.requestAnimationFrame(resize);
    });
    const timeoutId = window.setTimeout(resize, 150);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [
    autoResizeTextarea,
    selectedDayDateKey,
    selectedObjectiveValue,
    selectedNotesValue,
  ]);

  useEffect(() => {
    const trigger = stickyTriggerRef.current;
    if (!trigger) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsDayListSticky(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsDayListSticky(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "-12px 0px 0px 0px",
      },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [loading, weekStart]);

  const setObjective = useCallback((dateKey, value) => {
    setPlan((prev) => {
      const next = {
        ...prev,
        objectivesByDate: { ...prev.objectivesByDate, [dateKey]: value },
      };
      localStorage.setItem(plannerStorageKey(courseId, weekStart), JSON.stringify(next));
      schedulePersistPlannerDay(dateKey, next);
      return next;
    });
    markPlannerDayEdited(dateKey);
  }, [courseId, markPlannerDayEdited, schedulePersistPlannerDay, weekStart]);

  const setNotes = useCallback((dateKey, value) => {
    setPlan((prev) => {
      const next = {
        ...prev,
        notesByDate: { ...prev.notesByDate, [dateKey]: value },
      };
      localStorage.setItem(plannerStorageKey(courseId, weekStart), JSON.stringify(next));
      schedulePersistPlannerDay(dateKey, next);
      return next;
    });
    markPlannerDayEdited(dateKey);
  }, [courseId, markPlannerDayEdited, schedulePersistPlannerDay, weekStart]);

  const setResources = (dateKey, resources) => {
    setPlan((prev) => {
      const next = {
        ...prev,
        resourcesByDate: {
          ...prev.resourcesByDate,
          [dateKey]: toResourceLinks(resources),
        },
      };
      localStorage.setItem(plannerStorageKey(courseId, weekStart), JSON.stringify(next));
      schedulePersistPlannerDay(dateKey, next);
      return next;
    });
    markPlannerDayEdited(dateKey);
  };

  const setStrategyCards = useCallback((dateKey, cards) => {
    setPlan((prev) => {
      const next = {
        ...prev,
        strategyCardsByDate: {
          ...prev.strategyCardsByDate,
          [dateKey]: toStrategyCards(cards),
        },
      };
      localStorage.setItem(plannerStorageKey(courseId, weekStart), JSON.stringify(next));
      schedulePersistPlannerDay(dateKey, next);
      return next;
    });
    markPlannerDayEdited(dateKey);
  }, [courseId, markPlannerDayEdited, schedulePersistPlannerDay, weekStart]);

  const openStrategyPicker = (dateKey) => {
    setStrategyPickerDateKey(dateKey);
    setStrategyPickerQuery("");
    setStrategyPickerError("");
    setStrategyPickerRows([]);
    setAddingStrategyId(null);
    setStrategyPickerOpen(true);
  };

  const closeStrategyPicker = () => {
    setStrategyPickerOpen(false);
    setStrategyPickerDateKey(null);
    setStrategyPickerQuery("");
    setStrategyPickerError("");
    setStrategyPickerRows([]);
    setStrategyPickerLoading(false);
    setAddingStrategyId(null);
  };

  const strategyCardKey = (dateKey, index, card) =>
    `${dateKey}:${card?.strategy_id || card?.strategy_seed_key || card?.title || "strategy"}:${index}`;

  const toggleStrategyCardExpanded = (dateKey, index, card) => {
    const key = strategyCardKey(dateKey, index, card);
    setExpandedStrategyCardKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const removeTeacherStrategyCard = (dateKey, index) => {
    const currentCards = toStrategyCards(plan.strategyCardsByDate[dateKey]);
    setStrategyCards(
      dateKey,
      currentCards.filter((_, strategyIndex) => strategyIndex !== index),
    );
  };

  const addStrategyCardToDay = async (dateKey, strategyRow) => {
    if (!dateKey || !strategyRow?.id) return;
    setAddingStrategyId(strategyRow.id);
    setStrategyPickerError("");
    try {
      const response = await teacherAPI.getStrategyLearnMore(strategyRow.id);
      const payload = response?.data || {};
      const strategy = payload?.strategy || {};
      const learnMore = payload?.learnMore || {};

      const nextCard = {
        strategy_id: strategy.id || strategyRow.id || null,
        strategy_seed_key: learnMore.strategy_seed_key || strategy.seed_key || strategyRow.seed_key || null,
        title: strategy.title || strategyRow.title || "Untitled strategy",
        category: strategyRow.category || null,
        visibility_scope: strategy.visibility_scope || strategyRow.visibility_scope || null,
        summary: learnMore.summary || strategyRow.description || null,
        sample_usage: learnMore.sample_usage || null,
        how_to_use_steps: toTextArray(learnMore.how_to_use_steps),
        look_fors: toTextArray(learnMore.look_fors),
        source_title: learnMore.source_title || null,
        source_url: learnMore.source_url || null,
        source_pages: learnMore.source_pages || null,
        reference_url: strategy.reference_url || strategyRow.reference_url || null,
        added_at: new Date().toISOString(),
      };

      const currentCards = toStrategyCards(plan.strategyCardsByDate[dateKey]);
      const duplicate = currentCards.some(
        (card) =>
          (nextCard.strategy_id && card.strategy_id === nextCard.strategy_id)
          || (
            nextCard.strategy_seed_key
            && card.strategy_seed_key === nextCard.strategy_seed_key
          ),
      );
      if (duplicate) {
        setStrategyPickerError("That strategy is already added for this day.");
        return;
      }

      setStrategyCards(dateKey, [...currentCards, nextCard]);
      closeStrategyPicker();
    } catch (error) {
      setStrategyPickerError(
        String(error?.response?.data?.error || "Could not add strategy right now."),
      );
    } finally {
      setAddingStrategyId(null);
    }
  };

  useEffect(() => {
    if (!courseId) return;
    if (typeof window === "undefined") return;

    const raw = window.sessionStorage.getItem(STRATEGY_INSERT_DRAFT_KEY);
    if (!raw) return;

    let draft;
    try {
      draft = JSON.parse(raw);
    } catch (_error) {
      window.sessionStorage.removeItem(STRATEGY_INSERT_DRAFT_KEY);
      return;
    }

    const snippetText = String(draft?.snippetText || "").trim();
    const targetField = String(draft?.targetField || "notes").toLowerCase();
    const targetCourseId = String(draft?.courseId || "").trim();
    const targetDateKeyRaw = String(draft?.targetDateKey || "").trim();
    const targetDateKey =
      /^\d{4}-\d{2}-\d{2}$/.test(targetDateKeyRaw) ? targetDateKeyRaw : (selectedDayDateKey || "");

    if (!snippetText) {
      window.sessionStorage.removeItem(STRATEGY_INSERT_DRAFT_KEY);
      return;
    }
    if (targetCourseId && targetCourseId !== String(courseId)) {
      return;
    }

    const appendText = (current, addition) => {
      const base = String(current || "").trim();
      if (!base) return addition;
      return `${base}\n\n${addition}`;
    };

    if (!targetDateKey) return;

    const isDateInCurrentWeek = workWeekDates.includes(targetDateKey);
    if (!isDateInCurrentWeek) {
      const targetDate = fromDateKey(targetDateKey);
      if (!targetDate) {
        window.sessionStorage.removeItem(STRATEGY_INSERT_DRAFT_KEY);
        return;
      }
      const targetWeekStart = mondayOfWeek(targetDate);
      if (targetWeekStart && targetWeekStart !== weekStart) {
        setWeekStart(targetWeekStart);
      }
      setSelectedDateKey(targetDateKey);
      return;
    }

    if (selectedDayDateKey !== targetDateKey) {
      setSelectedDateKey(targetDateKey);
      return;
    }

    const currentObjective = plan.objectivesByDate[targetDateKey] || "";
    const currentNotes = plan.notesByDate[targetDateKey] || "";

    if (targetField === "objective") {
      setObjective(targetDateKey, appendText(currentObjective, snippetText));
    } else {
      setNotes(targetDateKey, appendText(currentNotes, snippetText));
    }

    window.sessionStorage.removeItem(STRATEGY_INSERT_DRAFT_KEY);
    setInsertNotice(
      targetField === "objective"
        ? `Inserted strategy into objective for ${formatDate(targetDateKey)}.`
        : `Inserted strategy into notes for ${formatDate(targetDateKey)}.`,
    );
    const timeoutId = window.setTimeout(() => setInsertNotice(""), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [
    courseId,
    plan.notesByDate,
    plan.objectivesByDate,
    setNotes,
    setObjective,
    selectedDayDateKey,
    weekStart,
    workWeekDates,
  ]);

  useEffect(() => {
    if (!strategyPickerOpen || !strategyPickerDateKey) return;

    let cancelled = false;
    const loadStrategies = async () => {
      setStrategyPickerLoading(true);
      setStrategyPickerError("");
      try {
        const response = await teacherAPI.listStrategies({
          q: strategyPickerQuery || undefined,
        });
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setStrategyPickerRows(rows);
      } catch (error) {
        if (cancelled) return;
        setStrategyPickerError(
          String(error?.response?.data?.error || "Could not load strategies right now."),
        );
        setStrategyPickerRows([]);
      } finally {
        if (!cancelled) setStrategyPickerLoading(false);
      }
    };

    loadStrategies();
    return () => {
      cancelled = true;
    };
  }, [strategyPickerDateKey, strategyPickerOpen, strategyPickerQuery]);

  const addTeacherResource = (dateKey) => {
    const title = window.prompt("Resource title (example: Exit Ticket Template)");
    if (!title || !title.trim()) return;
    const url = window.prompt("Resource URL (optional)");
    const notes = window.prompt("Resource notes (optional)");

    const currentResources = toResourceLinks(plan.resourcesByDate[dateKey]);
    setResources(dateKey, [
      ...currentResources,
      {
        title: title.trim(),
        url: String(url || "").trim(),
        notes: String(notes || "").trim(),
      },
    ]);
  };

  const removeTeacherResource = (dateKey, index) => {
    const currentResources = toResourceLinks(plan.resourcesByDate[dateKey]);
    setResources(
      dateKey,
      currentResources.filter((_, resourceIndex) => resourceIndex !== index),
    );
  };

  const copyWeekPlan = async () => {
    const lines = [];
    lines.push(`Weekly Plan (${weekStart} to ${weekEnd})`);
    lines.push("");
    weekRows.forEach(({ dateKey, row }) => {
      const lesson = row?.lesson_id ? lessonDetails[row.lesson_id] : null;
      lines.push(`${formatDate(dateKey)} (${dateKey})`);
      lines.push(`- Lesson: ${lesson?.lesson_title || row?.lesson_title || "None scheduled"}`);
      lines.push(`- TEKS: ${row?.teks_code || "N/A"}`);
      lines.push(`- Objective: ${plan.objectivesByDate[dateKey] || ""}`);
      lines.push(`- Notes: ${plan.notesByDate[dateKey] || ""}`);
      const teacherResources = toResourceLinks(plan.resourcesByDate[dateKey]);
      if (teacherResources.length > 0) {
        lines.push("- My Resources:");
        teacherResources.forEach((resource) => {
          const label = [resource.title, resource.url].filter(Boolean).join(" - ");
          lines.push(`  - ${label || resource.notes}`);
          if (resource.notes) lines.push(`    notes: ${resource.notes}`);
        });
      }
      const strategyCards = toStrategyCards(plan.strategyCardsByDate[dateKey]);
      if (strategyCards.length > 0) {
        lines.push("- Strategies:");
        strategyCards.forEach((strategyCard) => {
          lines.push(`  - ${strategyCard.title}`);
          if (strategyCard.summary) lines.push(`    summary: ${strategyCard.summary}`);
        });
      }
      if (row?.milestone) lines.push(`- Milestone: ${row.milestone}`);
      lines.push("");
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
    } catch (error) {
      console.error("Failed to copy planner:", error);
    }
  };

  const flushPlannerDayToServer = async (dateKey) => {
    if (!courseId || !dateKey) return;
    if (persistTimeoutsRef.current[dateKey]) {
      window.clearTimeout(persistTimeoutsRef.current[dateKey]);
      delete persistTimeoutsRef.current[dateKey];
    }
    await teacherAPI.upsertPlannerEntry({
      courseId,
      date: dateKey,
      objectiveOverride: plan.objectivesByDate[dateKey] || "",
      notes: plan.notesByDate[dateKey] || "",
      resourceLinks: toResourceLinks(plan.resourcesByDate[dateKey]),
      strategyCards: toStrategyCards(plan.strategyCardsByDate[dateKey]),
    });
  };

  const setPublishedMetaFromRows = useCallback((rows = []) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setPlannerPublishMetaByDate((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        const dateKey = toDateKey(row?.calendar_date);
        if (!dateKey) return;
        next[dateKey] = {
          publishedAt: row.published_at || row.publishedAt || null,
          updatedAt: row.updated_at || row.updatedAt || new Date().toISOString(),
          publishState: row.publish_state || row.publishState || "published",
        };
      });
      return next;
    });
  }, []);

  const publishPlannerDay = async (dateKey) => {
    if (!courseId || !dateKey) return;
    setPlannerPublishError("");
    setPlannerPublishSuccess("");
    setPublishingPlannerDateKey(dateKey);
    try {
      await flushPlannerDayToServer(dateKey);
      const response = await teacherAPI.publishPlannerEntry({ courseId, date: dateKey });
      setPublishedMetaFromRows([response.data || {}]);
      setPlannerPublishSuccess(`Published ${formatDate(dateKey)} for walkthrough.`);
    } catch (error) {
      const apiError = String(error?.response?.data?.error || "").trim();
      setPlannerPublishError(apiError || "Could not publish this day for walkthrough.");
    } finally {
      setPublishingPlannerDateKey(null);
    }
  };

  const publishPlannerWeekForWalkthrough = async () => {
    if (!courseId || !weekRows.length) return;
    const dates = weekRows.map((item) => item.dateKey).filter(Boolean);
    if (!dates.length) return;
    setPlannerPublishError("");
    setPlannerPublishSuccess("");
    setPublishingPlannerWeek(true);
    try {
      for (const dateKey of dates) {
        // Flush all local edits before creating publish snapshots.
        // eslint-disable-next-line no-await-in-loop
        await flushPlannerDayToServer(dateKey);
      }
      const response = await teacherAPI.publishPlannerWeek({ courseId, dates });
      const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
      setPublishedMetaFromRows(rows);
      const count = Number(response?.data?.count || rows.length || 0);
      setPlannerPublishSuccess(
        count > 0
          ? `Published ${count} day${count === 1 ? "" : "s"} for walkthrough.`
          : "No planner days were published.",
      );
    } catch (error) {
      const apiError = String(error?.response?.data?.error || "").trim();
      setPlannerPublishError(apiError || "Could not publish this week for walkthrough.");
    } finally {
      setPublishingPlannerWeek(false);
    }
  };

  const publishDayToKiosk = async (dateKey, row, lesson) => {
    if (!courseId || !dateKey) return;

    const structuredObjectives = parseObjectiveTextToStructured(
      plan.objectivesByDate[dateKey] || "",
      row?.teks_code || "",
    );
    const objectiveValidation = validateObjectivesForCourse(
      structuredObjectives,
      row?.teks_code || "",
    );
    if (!objectiveValidation.isValid) {
      setPublishErrorByDate((prev) => ({
        ...prev,
        [dateKey]:
          objectiveValidation.errors[0]
          || "Add a valid objective statement before publishing to kiosk.",
      }));
      return;
    }

    const serializedObjectiveText = serializeStructuredObjectives(structuredObjectives);
    const parsedObjective = parseObjectiveText(serializedObjectiveText);
    const imageUrl = (objectiveImagesByDate[dateKey] || [])[0] || "";
    const displayTitle = `${lesson?.lesson_title || row?.lesson_title || "Lesson"} - ${formatDate(dateKey)}`;
    const entry = {
      teksCode: row?.teks_code || "",
      statement:
        parsedObjective.statement ||
        "The student will demonstrate mastery of the targeted standard.",
      imageUrl,
      iCanStatements: parsedObjective.iCanStatements,
    };

    const payload = {
      displayTitle,
      dateKey,
      courseId,
      lessonId: row?.lesson_id || null,
      entries: [entry],
    };

    setPublishingDateKey(dateKey);
    setPublishedDateKey(null);
    setPublishErrorByDate((prev) => ({ ...prev, [dateKey]: "" }));

    try {
      await kioskAPI.saveMyObjectivesByDate({
        courseId,
        date: dateKey,
        displayTitle,
        entries: [entry],
      });

      const publishResponse = await kioskAPI.publishMy(payload);
      const channelKey = String(publishResponse?.data?.channelKey || "");

      // Keep local publish for same-browser immediate display support.
      publishKioskPayload(payload, channelKey || null);
      setPublishedDateKey(dateKey);
    } catch (error) {
      console.error("Error publishing planner day to kiosk:", error);
      const apiError = String(error?.response?.data?.error || "").trim();
      setPublishErrorByDate((prev) => ({
        ...prev,
        [dateKey]: apiError ? `Publish failed: ${apiError}` : "Publish failed. Try again.",
      }));
    } finally {
      setPublishingDateKey(null);
    }
  };

  const restoreDefaultObjectiveForDay = async (dateKey) => {
    if (!courseId || !dateKey) return;
    setRestoringDefaultDateKey(dateKey);
    try {
      const response = await kioskAPI.getObjectivesByDate(courseId, dateKey);
      const payload = response?.data || null;
      const entries = Array.isArray(payload?.entries) ? payload.entries : [];
      if (!entries.length) {
        setInsertNotice(`No district objective found for ${formatDate(dateKey)}.`);
        return;
      }

      const normalizedEntries = entries
        .map((entry, index) => ({
          order: Number(index + 1),
          teksCode: String(entry?.teksCode || "").trim(),
          statement: String(entry?.statement || "").trim(),
          imageUrl: String(entry?.imageUrl || "").trim(),
          iCanStatements: Array.isArray(entry?.iCanStatements)
            ? entry.iCanStatements
            : [],
        }))
        .sort((a, b) => a.order - b.order);

      const restoredObjectiveText = formatKioskEntriesForPlanner(normalizedEntries);
      if (!restoredObjectiveText) {
        setInsertNotice(`No district objective found for ${formatDate(dateKey)}.`);
        return;
      }

      setObjective(dateKey, restoredObjectiveText);
      setObjectiveImagesByDate((prev) => ({
        ...prev,
        [dateKey]: normalizedEntries.map((entry) => entry.imageUrl).filter(Boolean),
      }));
      setInsertNotice(`Restored district objective for ${formatDate(dateKey)}.`);
    } catch (error) {
      console.error("Error restoring default objective for planner day:", error);
      setInsertNotice("Could not restore district objective right now.");
    } finally {
      setRestoringDefaultDateKey(null);
      window.setTimeout(() => setInsertNotice(""), 3500);
    }
  };

  if (!courseId) {
    return (
      <div className="panel-card">
        <h2>Weekly Planner</h2>
        <p>Select a course to build a weekly plan.</p>
      </div>
    );
  }

  return (
    <div className="panel-card weekly-planner-page">
      <div className="planner-header" data-tour="planner-header">
        <div>
          <h2>Weekly Lesson Planner</h2>
          <p>Plan a full week with objectives, TEKS coverage, milestones, and resources.</p>
        </div>
        <div className="planner-header-actions">
          <ScopeSequenceViewer
            courseId={courseId}
            selectedUnitNumber={activeWeekUnitNumber}
          />
          <button
            type="button"
            className="primary-btn"
            onClick={publishPlannerWeekForWalkthrough}
            disabled={publishingPlannerWeek}
          >
            {publishingPlannerWeek ? "Publishing Week..." : "Publish Week for Walkthrough"}
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() => (window.location.hash = "/kiosk-control")}
          >
            Open Kiosk Control
          </button>
          <button type="button" className="primary-btn" onClick={copyWeekPlan} data-tour="planner-copy">
            Copy Week Plan
          </button>
        </div>
        {(plannerPublishSuccess || plannerPublishError) && (
          <div className="planner-publish-flash">
            {plannerPublishSuccess ? (
              <p className="planner-publish-success">{plannerPublishSuccess}</p>
            ) : null}
            {plannerPublishError ? (
              <p className="planner-publish-error">{plannerPublishError}</p>
            ) : null}
          </div>
        )}
      </div>

      <div className="planner-week-nav" data-tour="planner-week-nav">
        <button type="button" className="nav-lite-btn" onClick={() => setWeekStart(addDays(weekStart, -7))}>
          Previous Week
        </button>
        <button type="button" className="nav-lite-btn" onClick={() => setWeekStart(mondayOfWeek())}>
          This Week
        </button>
        <button type="button" className="nav-lite-btn" onClick={() => setWeekStart(addDays(weekStart, 7))}>
          Next Week
        </button>
        <div className="week-label">
          <strong>{weekStart}</strong> to <strong>{weekEnd}</strong>
        </div>
      </div>

      <div className="planner-kpis" data-tour="planner-kpis">
        <div className="planner-kpi">
          <span>Planned Days</span>
          <strong>{summary.plannedDays}</strong>
        </div>
        <div className="planner-kpi">
          <span>TEKS Covered</span>
          <strong>{summary.teks.length}</strong>
        </div>
        <div className="planner-kpi">
          <span>Milestones</span>
          <strong>{summary.milestoneCount}</strong>
        </div>
        <div className="planner-kpi">
          <span>Resources</span>
          <strong>{summary.totalResources}</strong>
        </div>
      </div>

      {loading && <p>Loading weekly planner...</p>}

      {!loading && (
        <>
          <div className="planner-sticky-trigger" ref={stickyTriggerRef} aria-hidden="true" />
          <div className="planner-layout planner-grid" data-tour="planner-grid">
          <aside
            className={`planner-day-list ${isDayListSticky ? "is-sticky" : ""}`}
            data-tour="planner-day-list"
          >
            {weekRows.map(({ dateKey, row }) => {
              const lesson = row?.lesson_id ? lessonDetails[row.lesson_id] : null;
              const isActive = selectedDay?.dateKey === dateKey;
              const publishMeta = plannerPublishMetaByDate[dateKey] || null;
              const publishState = toPlannerPublishState({
                publishedAt: publishMeta?.publishedAt || null,
                updatedAt: publishMeta?.updatedAt || null,
              });
              return (
                <button
                  type="button"
                  key={dateKey}
                  className={`planner-day-item ${isActive ? "active" : ""}`}
                  onClick={() => setSelectedDateKey(dateKey)}
                >
                  <div className="planner-day-item-title">{formatDate(dateKey)}</div>
                  <div className="planner-day-item-date">{dateKey}</div>
                  <div className="planner-day-item-lesson">
                    {lesson?.lesson_title || row?.lesson_title || "No scheduled lesson"}
                  </div>
                  <div className="planner-day-item-meta-row">
                    {row?.day_type ? (
                      <span className="planner-day-item-meta">{row.day_type} Day</span>
                    ) : null}
                    {row?.teks_code ? (
                      <span className="planner-day-item-meta">{row.teks_code}</span>
                    ) : null}
                  </div>
                  {row?.milestone ? (
                    <div className="planner-day-item-milestone">{row.milestone}</div>
                  ) : null}
                  <div className={`planner-day-publish-badge ${plannerPublishClass(publishState)}`}>
                    {plannerPublishLabel(publishState)}
                  </div>
                  {reviewedDates.has(dateKey) ? (
                    <div className="planner-day-reviewed-badge">Reviewed by principal</div>
                  ) : null}
                </button>
              );
            })}
          </aside>

            {selectedDayDateKey && (
            <article className="day-card planner-day-detail" key={selectedDayDateKey}>
                <header className="day-card-header">
                  <div>
                    <h3>{formatDate(selectedDayDateKey)}</h3>
                    <p className="day-key">{selectedDayDateKey}</p>
                  </div>
                  {selectedDayRow?.day_type && (
                    <span className="day-type">{selectedDayRow.day_type} Day</span>
                  )}
                </header>

                {selectedDayRow?.milestone && (
                  <div className="milestone-pill">{selectedDayRow.milestone}</div>
                )}

                <div className="lesson-block">
                  <h4>
                    {selectedDayLesson?.lesson_title ||
                      selectedDayRow?.lesson_title ||
                      "No scheduled lesson"}
                  </h4>
                  {selectedDayLesson?.chapter_ref && (
                    <p className="muted">Chapter: {selectedDayLesson.chapter_ref}</p>
                  )}
                  <div className="planner-detail-publish-row">
                    <span className={`planner-day-publish-badge ${plannerPublishClass(selectedDayPublishState)}`}>
                      {plannerPublishLabel(selectedDayPublishState)}
                    </span>
                    {selectedDayPublishMeta?.publishedAt ? (
                      <span className="muted">
                        Last publish: {new Date(selectedDayPublishMeta.publishedAt).toLocaleString()}
                      </span>
                    ) : (
                      <span className="muted">Publish this day when ready for principal walkthrough.</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="nav-lite-btn"
                    onClick={() => publishPlannerDay(selectedDayDateKey)}
                    disabled={publishingPlannerDateKey === selectedDayDateKey}
                  >
                    {publishingPlannerDateKey === selectedDayDateKey
                      ? "Publishing..."
                      : "Publish This Day for Walkthrough"}
                  </button>
                  <button
                    type="button"
                    className="nav-lite-btn"
                    onClick={() =>
                      publishDayToKiosk(
                        selectedDayDateKey,
                        selectedDayRow,
                        selectedDayLesson,
                      )
                    }
                    disabled={
                      publishingDateKey === selectedDayDateKey
                      || !selectedObjectiveValidation.isValid
                    }
                    title={
                      !selectedObjectiveValidation.isValid
                        ? selectedObjectiveValidation.errors[0]
                        : undefined
                    }
                  >
                    {publishingDateKey === selectedDayDateKey
                      ? "Publishing..."
                      : "Publish This Day to Kiosk Display"}
                  </button>
                  {publishedDateKey === selectedDayDateKey && (
                    <p className="muted">Published to kiosk.</p>
                  )}
                  {publishErrorByDate[selectedDayDateKey] && (
                    <p className="muted">{publishErrorByDate[selectedDayDateKey]}</p>
                  )}
                  {selectedDayRow?.essential_questions && (
                    <p className="muted">
                      <strong>Essential Question:</strong> {selectedDayRow.essential_questions}
                    </p>
                  )}
                </div>

                <div className="teks-row">
                  <span className="field-label">TEKS</span>
                  {selectedDayRow?.teks_code ? (
                    <button
                      type="button"
                      className="teks-chip"
                      data-tour="planner-teks-chip"
                      onClick={() => setSelectedTeksCode(selectedDayRow.teks_code)}
                    >
                      {selectedDayRow.teks_code}
                    </button>
                  ) : (
                    <span className="muted">None</span>
                  )}
                </div>

                <div className="field-group">
                  <div className="planner-objective-label-row">
                    <label htmlFor={`objective-${selectedDayDateKey}`} className="field-label">
                      Weekly Learning Objectives
                    </label>
                    <button
                      type="button"
                      className="planner-inline-text-btn"
                      onClick={() => restoreDefaultObjectiveForDay(selectedDayDateKey)}
                      disabled={restoringDefaultDateKey === selectedDayDateKey}
                    >
                      {restoringDefaultDateKey === selectedDayDateKey
                        ? "Restoring..."
                        : "Restore Default Objective"}
                    </button>
                  </div>
                  <div className="planner-objective-builder" id={`objective-${selectedDayDateKey}`}>
                    {selectedObjectiveRows.map((objective, objectiveIndex) => (
                      <div
                        key={`${selectedDayDateKey}-objective-${objectiveIndex}`}
                        className="planner-objective-card"
                      >
                        {(() => {
                          const objectivePickerKey = `${selectedDayDateKey}-objective-${objectiveIndex}`;
                          const isObjectivePickerActive =
                            activeObjectiveTeksKey === objectivePickerKey;
                          const objectiveTeksInputValue = isObjectivePickerActive
                            ? objectiveTeksSearchInput
                            : String(objective.teksCode || "");
                          const hasCourseTeksResults =
                            !courseTeksLoading && filteredCourseTeksOptions.length > 0;

                          return (
                            <>
                        <div className="planner-objective-card-head">
                          <strong>Objective {objectiveIndex + 1}</strong>
                          <button
                            type="button"
                            className="nav-lite-btn"
                            onClick={() => {
                              if (selectedObjectiveRows.length <= 1) return;
                              const next = selectedObjectiveRows.filter(
                                (_item, idx) => idx !== objectiveIndex,
                              );
                              setObjective(
                                selectedDayDateKey,
                                serializeStructuredObjectives(next),
                              );
                            }}
                            disabled={selectedObjectiveRows.length <= 1}
                          >
                            Remove
                          </button>
                        </div>

                        <div className="planner-objective-grid">
                          <label className="planner-objective-field planner-objective-teks-picker">
                            <span>TEKS</span>
                            <input
                              type="search"
                              value={objectiveTeksInputValue}
                              autoComplete="off"
                              data-teks-picker-key={objectivePickerKey}
                              placeholder={courseTeksLoading ? "Loading TEKS..." : "Search course TEKS"}
                              onFocus={(event) => {
                                setActiveObjectiveTeksKey(objectivePickerKey);
                                setObjectiveTeksSearchInput(String(objective.teksCode || ""));
                                window.requestAnimationFrame(() => {
                                  event.target.select();
                                });
                              }}
                              onChange={(event) => {
                                const nextValue = String(event.target.value || "");
                                setActiveObjectiveTeksKey(objectivePickerKey);
                                setObjectiveTeksSearchInput(nextValue);
                                const exactMatch = courseTeksOptions.find(
                                  (option) =>
                                    String(option.code || "").toLowerCase()
                                    === nextValue.trim().toLowerCase(),
                                );
                                if (!exactMatch) return;
                                const next = selectedObjectiveRows.map((item, idx) =>
                                  idx === objectiveIndex
                                    ? { ...item, teksCode: exactMatch.code }
                                    : item,
                                );
                                setObjective(selectedDayDateKey, serializeStructuredObjectives(next));
                              }}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter") return;
                                if (!hasCourseTeksResults) return;
                                event.preventDefault();
                                const firstMatch = filteredCourseTeksOptions[0];
                                const next = selectedObjectiveRows.map((item, idx) =>
                                  idx === objectiveIndex
                                    ? { ...item, teksCode: firstMatch.code }
                                    : item,
                                );
                                setObjective(selectedDayDateKey, serializeStructuredObjectives(next));
                                setObjectiveTeksSearchInput(firstMatch.code);
                                setActiveObjectiveTeksKey("");
                              }}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  if (activeObjectiveTeksKeyRef.current !== objectivePickerKey) return;
                                  setActiveObjectiveTeksKey("");
                                  setObjectiveTeksSearchInput("");
                                }, 120);
                              }}
                            />
                            {isObjectivePickerActive && (
                              <div className="planner-objective-teks-menu" role="listbox">
                                {courseTeksLoading && (
                                  <div className="planner-objective-teks-empty">Loading TEKS...</div>
                                )}
                                {!courseTeksLoading && hasCourseTeksResults && (
                                  filteredCourseTeksOptions.map((option) => (
                                    <button
                                      key={option.code}
                                      type="button"
                                      className="planner-objective-teks-option"
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        const next = selectedObjectiveRows.map((item, idx) =>
                                          idx === objectiveIndex
                                            ? { ...item, teksCode: option.code }
                                            : item,
                                        );
                                        setObjective(
                                          selectedDayDateKey,
                                          serializeStructuredObjectives(next),
                                        );
                                        setObjectiveTeksSearchInput(option.code);
                                        setActiveObjectiveTeksKey("");
                                      }}
                                    >
                                      <strong>{option.code}</strong>
                                      {option.description ? <span>{option.description}</span> : null}
                                    </button>
                                  ))
                                )}
                                {!courseTeksLoading && !hasCourseTeksResults && (
                                  <div className="planner-objective-teks-empty">
                                    {courseTeksError || "No TEKS match for that search."}
                                  </div>
                                )}
                              </div>
                            )}
                          </label>
                          <label className="planner-objective-field planner-objective-field-wide">
                            <span>Objective statement</span>
                            <textarea
                              value={objective.statement || ""}
                              onChange={(event) => {
                                const next = selectedObjectiveRows.map((item, idx) =>
                                  idx === objectiveIndex
                                    ? { ...item, statement: event.target.value }
                                    : item,
                                );
                                setObjective(
                                  selectedDayDateKey,
                                  serializeStructuredObjectives(next),
                                );
                              }}
                              rows={2}
                              placeholder="The student will..."
                            />
                          </label>
                        </div>

                        <div className="planner-ican-block">
                          <span className="field-label">I can statements</span>
                          {(objective.iCanStatements || []).map((line, lineIndex) => (
                            <div
                              key={`${selectedDayDateKey}-${objectiveIndex}-ican-${lineIndex}`}
                              className="planner-ican-row"
                            >
                              <input
                                type="text"
                                value={line}
                                onChange={(event) => {
                                  const next = selectedObjectiveRows.map((item, idx) => {
                                    if (idx !== objectiveIndex) return item;
                                    const nextLines = Array.isArray(item.iCanStatements)
                                      ? [...item.iCanStatements]
                                      : [];
                                    nextLines[lineIndex] = event.target.value;
                                    return { ...item, iCanStatements: nextLines };
                                  });
                                  setObjective(
                                    selectedDayDateKey,
                                    serializeStructuredObjectives(next),
                                  );
                                }}
                                placeholder="I can explain..."
                              />
                              <button
                                type="button"
                                className="nav-lite-btn"
                                onClick={() => {
                                  const next = selectedObjectiveRows.map((item, idx) => {
                                    if (idx !== objectiveIndex) return item;
                                    const nextLines = Array.isArray(item.iCanStatements)
                                      ? item.iCanStatements.filter(
                                        (_value, idx2) => idx2 !== lineIndex,
                                      )
                                      : [];
                                    return { ...item, iCanStatements: nextLines };
                                  });
                                  setObjective(
                                    selectedDayDateKey,
                                    serializeStructuredObjectives(next),
                                  );
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="nav-lite-btn"
                            onClick={() => {
                              const next = selectedObjectiveRows.map((item, idx) =>
                                idx === objectiveIndex
                                  ? {
                                    ...item,
                                    iCanStatements: [...(item.iCanStatements || []), ""],
                                  }
                                  : item,
                              );
                              setObjective(
                                selectedDayDateKey,
                                serializeStructuredObjectives(next),
                              );
                            }}
                          >
                            Add I can statement
                          </button>
                        </div>
                            </>
                          );
                        })()}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="nav-lite-btn"
                      onClick={() => {
                        const fallbackTeks = selectedDayRow?.teks_code || "";
                        const next = [
                          ...selectedObjectiveRows,
                          {
                            teksCode: fallbackTeks,
                            statement: "",
                            iCanStatements: [""],
                          },
                        ];
                        setObjective(
                          selectedDayDateKey,
                          serializeStructuredObjectives(next),
                        );
                      }}
                    >
                      Add Objective
                    </button>
                  </div>
                  {courseTeksError && (
                    <p className="planner-objective-guard-warning">{courseTeksError}</p>
                  )}
                  {!courseTeksLoading && !courseTeksError && courseTeksOptions.length === 0 && (
                    <p className="planner-objective-guard-warning">
                      No TEKS are available for this course yet. Add TEKS in Curriculum Hub first.
                    </p>
                  )}
                  {selectedObjectiveValidation.errors.map((message) => (
                    <p key={`objective-guard-error-${message}`} className="planner-objective-guard-error">
                      {message}
                    </p>
                  ))}
                  {selectedObjectiveValidation.warnings.map((message) => (
                    <p key={`objective-guard-warning-${message}`} className="planner-objective-guard-warning">
                      {message}
                    </p>
                  ))}
                </div>

                {insertNotice ? <p className="planner-insert-notice">{insertNotice}</p> : null}

                {(objectiveImagesByDate[selectedDayDateKey] || []).length > 0 && (
                  <div className="objective-images">
                    <span className="field-label">Objective Image</span>
                    {(objectiveImagesByDate[selectedDayDateKey] || []).map((imageUrl, idx) => (
                      <a
                        key={`${selectedDayDateKey}-objective-image-${idx}`}
                        href={imageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="objective-image-link"
                      >
                        <img
                          src={imageUrl}
                          alt={`Objective visual for ${formatDate(selectedDayDateKey)}`}
                          className="objective-image-preview"
                        />
                      </a>
                    ))}
                  </div>
                )}

                <div className="field-group">
                  <label htmlFor={`notes-${selectedDayDateKey}`} className="field-label">
                    Teacher Notes / Checks
                  </label>
                  <textarea
                    ref={notesTextareaRef}
                    id={`notes-${selectedDayDateKey}`}
                    value={selectedNotesValue}
                    onChange={(e) => {
                      setNotes(selectedDayDateKey, e.target.value);
                      autoResizeTextarea(e.target);
                    }}
                    onFocus={(e) => autoResizeTextarea(e.target)}
                    placeholder="Materials, formative checks, accommodations, reminders..."
                    rows={3}
                  />
                </div>

                <div className="resources-block">
                  <span className="field-label">Resources</span>
                  {selectedDayAssets.length === 0 && <p className="muted">No linked resources.</p>}
                  {selectedDayAssets.length > 0 && (
                    <ul>
                      {selectedDayAssets.map((asset) => (
                        <li key={asset.id}>
                          {asset.url || performanceTaskPathByAsset(asset) ? (
                            <a
                              href={asset.url || `#${performanceTaskPathByAsset(asset)}`}
                              target={asset.url ? "_blank" : undefined}
                              rel={asset.url ? "noreferrer" : undefined}
                            >
                              {asset.title}
                            </a>
                          ) : (
                            asset.title
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  <span className="field-label">My Resources</span>
                  {toResourceLinks(plan.resourcesByDate[selectedDayDateKey]).length === 0 && (
                    <p className="muted">No personal resources added yet.</p>
                  )}
                  {toResourceLinks(plan.resourcesByDate[selectedDayDateKey]).length > 0 && (
                    <ul>
                      {toResourceLinks(plan.resourcesByDate[selectedDayDateKey]).map(
                        (resource, index) => (
                        <li key={`${selectedDayDateKey}-resource-${index}`}>
                          {resource.url ? (
                            <a href={resource.url} target="_blank" rel="noreferrer">
                              {resource.title || resource.url}
                            </a>
                          ) : (
                            resource.title || "Untitled resource"
                          )}
                          {resource.notes && <span className="muted"> ({resource.notes})</span>}
                          <button
                            type="button"
                            className="nav-lite-btn"
                            onClick={() => removeTeacherResource(selectedDayDateKey, index)}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <span className="field-label">Strategies for this day</span>
                  {selectedDayStrategyCards.length === 0 && (
                    <p className="muted">No strategies added yet.</p>
                  )}
                  {selectedDayStrategyCards.length > 0 && (
                    <div className="planner-strategy-cards">
                      {selectedDayStrategyCards.map((strategyCard, index) => {
                        const cardKey = strategyCardKey(
                          selectedDayDateKey,
                          index,
                          strategyCard,
                        );
                        const isExpanded = Boolean(expandedStrategyCardKeys[cardKey]);
                        return (
                          <article
                            key={`${selectedDayDateKey}-strategy-${index}`}
                            className={`planner-strategy-card ${isExpanded ? "expanded" : "collapsed"}`}
                          >
                            <header className="planner-strategy-card-header">
                              <button
                                type="button"
                                className="planner-strategy-toggle"
                                onClick={() =>
                                  toggleStrategyCardExpanded(
                                    selectedDayDateKey,
                                    index,
                                    strategyCard,
                                  )
                                }
                                aria-expanded={isExpanded}
                              >
                                <span className="planner-strategy-toggle-label">
                                  {strategyCard.title}
                                </span>
                                <span className="planner-strategy-toggle-icon">
                                  {isExpanded ? "Hide" : "Open"}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="nav-lite-btn"
                                onClick={() =>
                                  removeTeacherStrategyCard(selectedDayDateKey, index)
                                }
                              >
                                Remove
                              </button>
                            </header>
                            {isExpanded && (
                              <div className="planner-strategy-card-body">
                                {strategyCard.summary ? <p>{strategyCard.summary}</p> : null}
                                {strategyCard.sample_usage ? (
                                  <p className="planner-strategy-sample">
                                    <strong>Sample:</strong> {strategyCard.sample_usage}
                                  </p>
                                ) : null}
                                {Array.isArray(strategyCard.how_to_use_steps)
                                && strategyCard.how_to_use_steps.length > 0 ? (
                                  <>
                                    <strong>How to use</strong>
                                    <ol>
                                      {strategyCard.how_to_use_steps.map((step, stepIndex) => (
                                        <li key={`${selectedDayDateKey}-${index}-step-${stepIndex}`}>
                                          {step}
                                        </li>
                                      ))}
                                    </ol>
                                  </>
                                  ) : null}
                                {Array.isArray(strategyCard.look_fors)
                                && strategyCard.look_fors.length > 0 ? (
                                  <>
                                    <strong>Look-fors</strong>
                                    <ul>
                                      {strategyCard.look_fors.map((item, lookIndex) => (
                                        <li key={`${selectedDayDateKey}-${index}-look-${lookIndex}`}>
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                  ) : null}
                                {(strategyCard.source_url || strategyCard.reference_url) ? (
                                  <a
                                    href={strategyCard.source_url || strategyCard.reference_url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {strategyCard.source_title || "Open source"}
                                  </a>
                                ) : null}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  )}

                  <div className="planner-resource-actions">
                    <button
                      type="button"
                      className="nav-lite-btn"
                      onClick={() => addTeacherResource(selectedDayDateKey)}
                    >
                      Add Personal Resource
                    </button>
                    <button
                      type="button"
                      className="nav-lite-btn"
                      onClick={() => openStrategyPicker(selectedDayDateKey)}
                    >
                      Add Strategy
                    </button>
                  </div>
                </div>
              </article>
            )}
          </div>
        </>
      )}

      {strategyPickerOpen && strategyPickerDateKey ? (
        <div
          className="strategy-picker-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Select strategy for planner day"
          onClick={closeStrategyPicker}
        >
          <div className="strategy-picker-card" onClick={(event) => event.stopPropagation()}>
            <div className="strategy-picker-header">
              <h3>Add Strategy for {formatDate(strategyPickerDateKey)}</h3>
              <button type="button" className="nav-lite-btn" onClick={closeStrategyPicker}>
                Close
              </button>
            </div>
            <input
              type="search"
              value={strategyPickerQuery}
              onChange={(event) => setStrategyPickerQuery(event.target.value)}
              placeholder="Search strategies"
              aria-label="Search strategies"
            />
            {strategyPickerLoading ? <p>Loading strategies...</p> : null}
            {!strategyPickerLoading && strategyPickerError ? (
              <p className="muted">{strategyPickerError}</p>
            ) : null}
            {!strategyPickerLoading && !strategyPickerError && strategyPickerRows.length === 0 ? (
              <p className="muted">No strategies matched this search.</p>
            ) : null}
            {!strategyPickerLoading && !strategyPickerError && strategyPickerRows.length > 0 ? (
              <div className="strategy-picker-list">
                {strategyPickerRows.map((strategyRow) => (
                  <article key={strategyRow.id} className="strategy-picker-item">
                    <div>
                      <h4>{strategyRow.title}</h4>
                      {strategyRow.description ? <p>{strategyRow.description}</p> : null}
                      <div className="strategy-picker-meta">
                        <span>{String(strategyRow.category || "").replace(/_/g, " ")}</span>
                        <span>
                          {strategyRow.visibility_scope === "teacher" ? "My strategy" : "District"}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => addStrategyCardToDay(strategyPickerDateKey, strategyRow)}
                      disabled={addingStrategyId === strategyRow.id}
                    >
                      {addingStrategyId === strategyRow.id ? "Adding..." : "Add"}
                    </button>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {selectedTeksCode && (
        <TEKSModal
          teksCode={selectedTeksCode}
          courseId={courseId}
          onClose={() => setSelectedTeksCode(null)}
        />
      )}
    </div>
  );
};

export default WeeklyPlannerPage;
